import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { AppStateService, FlatMenuItem, HierarchyMenuItem } from '@siza-staff-care/shared/app-state';
import { camelCase, startCase } from 'lodash-es';
import { tap } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { menuStructure } from './menu-routes/menu-structure';
import { securityMappings } from './menu-routes/security-mappings';

export const enum SecurityConstants {
	Access_Full = 'F',
	Access_None = 'N',
	Access_ReadOnly = 'R',
	Access_Group = 'G',
	Access_Mandatory = 'M',
}

export type FlattenedItem = {
	displayPath: string;
	paths: string[];
	pathWords: string[];
	key: string;
	item: HierarchyMenuItem;
};

// Mappings for screen aliases - that is, menu entries that map to the same screen
// as other entries. Single instance of this: StaffMaintenanceEndUser -> StaffMaintenance.
// Note - this is used for control security mappings only - not for menu access.
// So, the user can have access to My Record (StaffMaintenanceEndUser) but not to
// Staff Records (StaffMaintenance).
const otherMappings = {
	[securityMappings.StaffMaintenanceEndUser]: securityMappings.StaffMaintenance,
	[securityMappings.ViewRequestsEndUser]: securityMappings.ViewRequests,
};

// Special case for dashboards - which are a root-level menu
const menuKeyOverrides = {
	'm:Dashboards': 'dashboards-menu',
};

type State = {
	menuHierarchy?: HierarchyMenuItem[];
	flattenedItems?: FlattenedItem[];
	flattenedItemsByKey?: Record<string, FlattenedItem>;
};

@Injectable({
	providedIn: 'root',
})
export class MenuBuilderService extends ComponentStore<State> {
	readonly _menuHierarchy$ = this.select(({ menuHierarchy }) => menuHierarchy ?? []);

	menuHierarchy$ = this._menuHierarchy$.pipe(
		tap((menuHierarchy) => {
			const flattenedItems = this.buildFlatItems(menuHierarchy);
			const flattenedItemsByKey = flattenedItems.reduce(
				(acc, item) => {
					acc[item.key] = item;
					return acc;
				},
				{} as Record<string, FlattenedItem>,
			);

			this.patchState({
				flattenedItems,
				flattenedItemsByKey,
			});
		}),
		shareReplay({ bufferSize: 1, refCount: true }),
	);

	menuItemsByKey: Record<string, HierarchyMenuItem> = {};

	/**
	 * A mapping of menu keys to a function that determines whether the feature is enabled (at a system level, not a user level).
	 */
	private featureFilters = {
		[securityMappings.PublicApiKeysAdmin]: () => this.appState.settings.enablePublicApi,
		[securityMappings.ExternalTA]: () => this.appState.settings.enableExternalTandA,
		[securityMappings.ItemsMatrix]: () => this.appState.settings.itemsMatrix,

		[securityMappings.ExpenseForms]: () => this.appState.settings.enableExpenses,
		[securityMappings.ExpensesQuery]: () => this.appState.settings.enableExpenses,
		[securityMappings.ExpensesReport]: () => this.appState.settings.enableExpenses,

		[securityMappings.ResourceAdmin]: () => this.appState.settings.roomBookings,
		[securityMappings.ResourceBookings]: () => this.appState.settings.roomBookings,
		[securityMappings.StarterLeaverReasonsAdmin]: () => this.appState.settings.enableStarterLeaverReasons,
		[securityMappings.StartersLeaversReport]: () => this.appState.settings.enableStarterLeaverReasons,
		[securityMappings.AuthenticatorDevices]: () => this.appState.settings.enableTotp,

		[securityMappings.EventViewer]: () => this.appState.settings.enableEventViewer,
		[securityMappings.SkillViewer]: () => this.appState.settings.enableSkillViewer,
	};

	get flattenedItems() {
		return this.get().flattenedItems;
	}

	constructor(private appState: AppStateService) {
		super({});

		this.watchFlatMenuChange();
		this.watchForRebuildMenus();
	}

	/**
	 * Gets the HierarchyMenuItem for the key, being the Jade form class name.
	 * @param formClassName
	 * @returns
	 */
	getMenuItem(formClassName: string) {
		return this.menuItemsByKey[formClassName];
	}

	/**
	 * Gets the HierarchyMenuItem for the key, being the Jade form class name. Special case for reports - if not found, tries again
	 * with the Report suffix removed.
	 * @param reportClassName
	 * @returns
	 */
	getReportMenuItem(reportClassName: string) {
		let menu = this.getMenuItem(reportClassName);
		if (!menu && reportClassName.endsWith('Report')) {
			menu = this.getMenuItem(reportClassName.substring(0, reportClassName.length - 'Report'.length));
		}

		return menu;
	}

	/**
	 * Gets the display path for the menu item by key, being the Jade form class name.
	 * @param formClassName
	 * @returns
	 */
	getDisplayPath(formClassName: string) {
		const flattenedItemsByKey = this.get().flattenedItemsByKey;
		const flattenedItem = flattenedItemsByKey?.[formClassName];
		return flattenedItem?.displayPath || `${formClassName} (not found)`;
	}

	/**
	 * Translates security keys	 * e.g. StaffMaintenanceEndUser -> StaffMaintenance, ViewRequestsEndUser -> ViewRequests
	 * @param key
	 * @returns
	 */
	getTranslatedKey(key: string) {
		key = otherMappings[key] ?? key;
		return key;
	}

	/**
	 * Builds a flattened list of all the menu entries, for searching purposes.
	 */
	private buildFlatItems(items: HierarchyMenuItem[]): FlattenedItem[] {
		if (!items) {
			return [];
		}

		const flatten = (item: HierarchyMenuItem, paths: string[], result: FlattenedItem[]) => {
			const displayPath = [...paths, item.name].join(' > ');

			// Extract all the words along the path
			const joinedPathWords = [...paths, item.name]
				.map((word) => word.toLowerCase())
				.join(' ')
				.split(/[^0-9A-Za-z&]/);

			const specialStartCase = (s: string) => {
				// Special case to handle the Leaves/Shifts menu item.
				// Without the special case, the / gets removed, which looks odd.
				if (s.includes('/')) {
					return s
						.split('/')
						.map((w) => startCase(w))
						.join('/');
				}
				return startCase(s);
			};

			// Construct the flattened item.
			const newItem: FlattenedItem = {
				displayPath: displayPath,
				paths: [...paths, specialStartCase(item.name)],
				pathWords: joinedPathWords,
				key: item.key,
				item,
			};
			result.push(newItem);

			if (!item.children) {
				return;
			}

			// Recurse into the children.
			item.children.forEach((child) => flatten(child, newItem.paths, result));
		};

		const result = [];
		items.forEach((i) => {
			flatten(i, [], result);
		});

		return result;
	}

	private readonly watchFlatMenuChange = this.effect(() =>
		this.appState.flatMenuItems$.pipe(
			tap((flatMenuItems) => {
				this.rebuildMenuStructure(flatMenuItems);
			}),
		),
	);

	private readonly watchForRebuildMenus = this.effect(() =>
		this.appState.rebuildMenus$.pipe(
			tap(() => {
				this.rebuildMenuStructure(this.appState.flatMenuItems);
			}),
		),
	);

	private rebuildMenuStructure(flatMenuItems: FlatMenuItem[] | undefined) {
		// The flatMenuItems lists all the items and their security level.

		// Construct the final menu structure according to what the user has access to.

		if (!flatMenuItems) {
			this.patchState({
				menuHierarchy: [],
			});
			return;
		}

		const accessLevelMap: Record<string, string> = flatMenuItems?.reduce((map, item: FlatMenuItem) => {
			map[item.key] = item.accessLevel;
			return map;
		}, {});

		const filterMenuItemsForEnabledFeatures = (itemKey: string) => {
			const filter = this.featureFilters[itemKey];
			if (typeof filter === 'function') {
				return filter();
			}

			return true;
		};

		const filterMenuStructure = (hierarchyItem: HierarchyMenuItem): HierarchyMenuItem | undefined => {
			let allowed = false;
			let level = '';
			let isMenu = false;

			if (hierarchyItem.key.endsWith('-menu')) {
				allowed = true;
				isMenu = true;
			} else {
				if (!filterMenuItemsForEnabledFeatures(hierarchyItem.key)) {
					allowed = false;
				} else {
					// Note - not translating the key - for this purpose Staff Records and My Record are different.
					// but when the screen is opened, or security configuration designed, they're the same.
					// Additionally, there's a special case for dashboards - which need to be treated as a menu.
					level = accessLevelMap[menuKeyOverrides[hierarchyItem.key] ?? hierarchyItem.key];
					allowed = level !== SecurityConstants.Access_None;
				}
			}

			if (!allowed) {
				return;
			}

			const item: HierarchyMenuItem = {
				name: this.translateMenuName(hierarchyItem),
				key: hierarchyItem.key,
				accessLevel: level,
				route: hierarchyItem.route,
				path: hierarchyItem.path,
			};

			if (hierarchyItem.children) {
				hierarchyItem.children.forEach((child) => {
					const childItem = filterMenuStructure(child);
					if (childItem) {
						if (!item.children) {
							item.children = [];
						}

						item.children.push(childItem);
					}
				});
			}

			// "menu" menu items with no children shouldn't appear.
			if (isMenu && !item.children?.length) {
				return;
			}

			return item;
		};

		const allocationManagementName = this.appState.settings.allocationManagementName ?? '';
		const allocationManagementScreenName = this.appState.settings.allocationManagementScreenName ?? '';

		let topLevelItems: HierarchyMenuItem[] = [];

		const assignPaths = (item: HierarchyMenuItem, path = '') => {
			const thisPath = path ? `${path}-${camelCase(item.name)}` : camelCase(item.name);
			item.path = thisPath;
			if (item.children) {
				item.children.forEach((child) => assignPaths(child, thisPath));
			}
		};

		const builtStructure = menuStructure(
			allocationManagementScreenName,
			allocationManagementName,
			this.appState.settings.sexAsGender,
		);
		builtStructure.forEach((item) => {
			assignPaths(item);

			const filteredItem = filterMenuStructure(item);
			if (filteredItem) {
				topLevelItems.push(filteredItem);
			}
		});

		// Prune any menus that have no children.
		const prune = (items: HierarchyMenuItem[]) => {
			const pruned: HierarchyMenuItem[] = [];
			items.forEach((item) => {
				// If the item doesn't have children, it's a leaf node. Add it to the pruned list.
				if (!item.children) {
					pruned.push(item);
				} else {
					// The item has children. Recurse and get the pruned list of children.
					const prunedChildren = prune(item.children);
					if (prunedChildren.length) {
						// If there are still children, add the item to the pruned list.
						pruned.push(item);
					}
				}
			});

			return pruned;
		};

		topLevelItems = prune(topLevelItems);
		this.buildMenuMap(topLevelItems);

		this.patchState({
			menuHierarchy: topLevelItems,
		});
	}

	private buildMenuMap(topLevelItems: HierarchyMenuItem[]) {
		const map: Record<string, HierarchyMenuItem> = {};
		const recurse = (items: HierarchyMenuItem[]) => {
			items.forEach((item) => {
				map[item.key] = item;
				if (item.children) {
					recurse(item.children);
				}
			});
		};

		recurse(topLevelItems);
		this.menuItemsByKey = map;
	}

	/**
	 * Handle special cases for dynamic renaming of menu items. (At present, used for resource bookings)
	 * @param item The item to check for replacement.
	 * @returns
	 */
	private translateMenuName(item: HierarchyMenuItem): string {
		switch (item.key) {
			case securityMappings.ResourceBookings:
				return this.appState.settings.allocationManagementScreenName ?? item.name;

			case securityMappings.ResourceAdmin:
				return this.appState.settings.allocationManagementName ?? item.name;
		}

		return item.name;
	}
}
